import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  logoutBoardSession, getTreasurerData,
  approveTreasurerBatch, blockTreasurerBatch,
} from "@workspace/api-client-react";
import type { TreasurerData } from "@workspace/api-client-react";
import {
  ArrowLeft, Loader2, DollarSign, TrendingUp, Send, CheckCircle2,
  Clock, XCircle, RefreshCw, LogOut, Building2, CheckCircle, Ban,
  CreditCard, AlertTriangle,
} from "lucide-react";

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const POOL_STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  funded: "bg-blue-100 text-blue-800 border-blue-200",
  disbursed: "bg-purple-100 text-purple-800 border-purple-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
};

const BATCH_STATUS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending_approval: { label: "Awaiting Approval", color: "bg-blue-100 text-blue-800 border-blue-200", icon: <Clock className="h-3 w-3" /> },
  pending: { label: "Processing", color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: <Clock className="h-3 w-3" /> },
  sent: { label: "Payment Sent", color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: <Send className="h-3 w-3" /> },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-800 border-green-200", icon: <CheckCircle2 className="h-3 w-3" /> },
  failed: { label: "Failed", color: "bg-red-100 text-red-800 border-red-200", icon: <XCircle className="h-3 w-3" /> },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800 border-red-200", icon: <XCircle className="h-3 w-3" /> },
};

function StatCard({ label, value, sub, icon: Icon, accent }: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  accent: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 flex items-start gap-4">
      <div className={`rounded-lg p-2.5 ${accent}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className="text-2xl font-bold tracking-tight mt-0.5">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function TreasurerDashboard() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery<TreasurerData>({
    queryKey: ["treasurer-data"],
    queryFn: getTreasurerData,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => approveTreasurerBatch(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["treasurer-data"] });
      toast({ title: "Payout batch approved and queued for processing" });
    },
    onError: () => toast({ title: "Failed to approve batch", variant: "destructive" }),
  });

  const blockMutation = useMutation({
    mutationFn: (id: number) => blockTreasurerBatch(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["treasurer-data"] });
      toast({ title: "Payout batch blocked" });
    },
    onError: () => toast({ title: "Failed to block batch", variant: "destructive" }),
  });

  async function handleLogout() {
    await logoutBoardSession();
    navigate("/board-entry");
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/board-entry")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Exit
            </Button>
            <LogoMark size={26} />
            <div>
              <span className="text-sm font-semibold text-white">Treasurer Portal</span>
              <span className="ml-2 text-xs text-slate-400 hidden sm:inline">DebtBridge 501(c)(3)</span>
            </div>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            {dataUpdatedAt > 0 && (
              <span className="text-xs text-slate-400 hidden sm:inline">
                Updated {fmtDateTime(new Date(dataUpdatedAt).toISOString())}
              </span>
            )}
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => void refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => void handleLogout()}>
              <LogOut className="h-4 w-4 mr-1" />
              Sign out
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Treasurer Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Financial summary, payout batch approvals, pool health, and payment method status.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-800 text-sm">
            Failed to load financial data. Please refresh.
          </div>
        ) : (
          <>
            {/* ── Financial Summary ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-primary" />
                Financial Summary
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="Total Donated"
                  value={fmt(data.financials.totalDonations)}
                  sub={`${data.financials.donationCount.toLocaleString()} donations`}
                  icon={TrendingUp}
                  accent="bg-primary"
                />
                <StatCard
                  label="Total Paid Out"
                  value={fmt(data.financials.totalPaidOut)}
                  sub={`${data.financials.paidBatchCount} confirmed batches`}
                  icon={Send}
                  accent="bg-emerald-600"
                />
                <StatCard
                  label="Net Reserve"
                  value={fmt(data.financials.netReserve)}
                  sub="across all pools"
                  icon={DollarSign}
                  accent="bg-blue-600"
                />
                <StatCard
                  label="Pending Batches"
                  value={String(data.pendingBatches.length)}
                  sub="awaiting your approval"
                  icon={Clock}
                  accent={data.pendingBatches.length > 0 ? "bg-amber-500" : "bg-slate-400"}
                />
              </div>
            </section>

            {/* ── Pending Approval Batches ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                Batches Awaiting Your Approval
                {data.pendingBatches.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    {data.pendingBatches.length} pending
                  </Badge>
                )}
              </h2>
              {data.pendingBatches.length === 0 ? (
                <div className="rounded-xl border p-6 text-center text-sm text-muted-foreground">
                  No payout batches are currently awaiting approval.
                </div>
              ) : (
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Batch</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Method</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Debts</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Date</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pendingBatches.map((b) => (
                        <tr key={b.id} className="border-t">
                          <td className="px-4 py-3 text-muted-foreground font-mono">#{b.id}</td>
                          <td className="px-4 py-3 capitalize text-sm">{b.paymentMethod}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-amber-700">{fmt(b.totalAmount)}</td>
                          <td className="px-4 py-3 text-right text-muted-foreground hidden sm:table-cell">{b.debtsCount}</td>
                          <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">{fmtDate(b.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                disabled={approveMutation.isPending || blockMutation.isPending}
                                onClick={() => approveMutation.mutate(b.id)}
                              >
                                <CheckCircle className="h-3 w-3" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs gap-1 border-red-300 text-red-700 hover:bg-red-50"
                                disabled={approveMutation.isPending || blockMutation.isPending}
                                onClick={() => blockMutation.mutate(b.id)}
                              >
                                <Ban className="h-3 w-3" />
                                Block
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* ── Payment Method Status ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                Creditor Payment Method Status
              </h2>
              {data.creditorBanking.total === 0 ? (
                <div className="rounded-xl border p-6 text-center text-sm text-muted-foreground">
                  No creditor payment methods on file yet.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl border p-4 text-center">
                    <p className="text-2xl font-bold">{data.creditorBanking.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total creditors</p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{data.creditorBanking.verified}</p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Verified
                    </p>
                  </div>
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">{data.creditorBanking.pendingVerification}</p>
                    <p className="text-xs text-amber-600 mt-1 flex items-center justify-center gap-1">
                      <Clock className="h-3 w-3" /> Pending verification
                    </p>
                  </div>
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                    <p className="text-2xl font-bold text-red-700">{data.creditorBanking.failed}</p>
                    <p className="text-xs text-red-600 mt-1 flex items-center justify-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Failed
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* ── Pool Health ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                Relief Pool Financial Status
              </h2>
              <div className="rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Pool</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Balance</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Goal</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Funded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pools.map((pool) => (
                      <tr key={pool.id} className="border-t">
                        <td className="px-4 py-3 font-medium">{pool.name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-xs capitalize ${POOL_STATUS_COLORS[pool.status] ?? ""}`}>
                            {pool.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{fmt(pool.balance)}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground font-mono text-sm">{fmt(pool.threshold)}</td>
                        <td className="px-4 py-3 text-right hidden sm:table-cell">
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full bg-primary" style={{ width: `${pool.percentFunded}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground w-8 text-right">{pool.percentFunded}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data.pools.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">No pools found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── Recent Batches ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Send className="h-4 w-4 text-primary" />
                Recent Payout Batches
              </h2>
              {data.recentBatches.length === 0 ? (
                <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">No payout batches yet.</div>
              ) : (
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Batch</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Debts</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentBatches.map((batch) => {
                        const cfg = BATCH_STATUS[batch.status] ?? BATCH_STATUS.sent;
                        return (
                          <tr key={batch.id} className="border-t">
                            <td className="px-4 py-3 text-muted-foreground font-mono">#{batch.id}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                                {cfg.icon}
                                {cfg.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono font-medium">{fmt(batch.totalAmount)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground hidden sm:table-cell">{batch.debtsCount}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground text-xs whitespace-nowrap">{fmtDate(batch.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="text-center pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                DebtBridge 501(c)(3) · Treasurer portal · Financial oversight and payout approvals
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
